import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { PlenaLogo } from "../components/PlenaLogo";
import KanbanBoard from "../components/KanbanBoard";
import TimelineView from "../components/TimelineView";
import ReportView from "../components/ReportView";
import TaskDetailSheet from "../components/TaskDetailSheet";
import TaskFilterBar from "../components/TaskFilterBar";
import ImportExcelDialog from "../components/ImportExcelDialog";
import ListView from "../components/ListView";
import { EMPTY_FILTERS, matchesFilters } from "../lib/taskFilters";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { LayoutGrid, GanttChartSquare, BarChart3, ChevronDown, Plus, LogOut, ArrowLeft, FolderKanban, Moon, Sun, FileSpreadsheet, Trash2, List } from "lucide-react";

export default function Workspace() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [board, setBoard] = useState(null);
  const [view, setView] = useState("board");
  const [selectedTask, setSelectedTask] = useState(null);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const isAdmin = user?.role === "admin";
  const visibleIds = useMemo(() => new Set((board?.tasks || []).filter((t) => matchesFilters(t, filters)).map((t) => t.id)), [board, filters]);
  const filteredBoard = useMemo(() => (board ? { ...board, tasks: board.tasks.filter((t) => visibleIds.has(t.id)) } : null), [board, visibleIds]);

  const loadWorkspace = useCallback(async () => {
    const { data } = await api.get("/workspace", { params: customerId ? { customer_id: customerId } : {} });
    setWorkspace(data);
    const remembered = localStorage.getItem(`plena-project-${data.customer.id}`);
    setProjectId((prev) => {
      const pick = [prev, remembered].find((id) => id && data.projects.some((p) => p.id === id));
      return pick || data.projects[0]?.id || null;
    });
  }, [customerId]);

  useEffect(() => {
    if (workspace && projectId) localStorage.setItem(`plena-project-${workspace.customer.id}`, projectId);
  }, [workspace, projectId]);

  const loadBoard = useCallback(async () => {
    if (!projectId) { setBoard(null); return; }
    const { data } = await api.get(`/projects/${projectId}/board`);
    setBoard(data);
  }, [projectId]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const createProject = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", { customer_id: workspace.customer.id, name: newProjectName });
      setNewProjectOpen(false);
      setNewProjectName("");
      await loadWorkspace();
      setProjectId(data.id);
      toast.success("Proje oluşturuldu");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const currentProject = workspace?.projects.find((p) => p.id === projectId);
  const customer = workspace?.customer;

  const deleteProject = async () => {
    if (!currentProject) return;
    const typed = window.prompt(`"${currentProject.name}" projesi ve ${currentProject.task_count} task'ı kalıcı olarak silinecek.\nOnaylamak için proje adını yazın:`);
    if (typed === null) return;
    if (typed.trim() !== currentProject.name.trim()) { toast.error("Proje adı eşleşmedi, silme iptal edildi"); return; }
    try {
      await api.delete(`/projects/${currentProject.id}`);
      toast.success("Proje silindi");
      setProjectId(null);
      await loadWorkspace();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openTask = (task) => setSelectedTask(task);

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#161618] flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-[#1C1C1E]/80 border-b border-slate-200/70 dark:border-white/10">
        <div className="px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {isAdmin && (
              <button data-testid="header-back-button" onClick={() => navigate("/admin")}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors shrink-0">
                <ArrowLeft size={15} />
              </button>
            )}
            <div data-testid="header-customer-logo" className="flex items-center gap-2.5 min-w-0">
              {customer?.logo_path ? (
                <img src={`${API}/files/${customer.logo_path}`} alt={customer.name} crossOrigin="use-credentials"
                  className="w-8 h-8 rounded-lg object-contain bg-slate-50 border border-slate-200/60 p-1" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center text-white font-display font-semibold text-sm shrink-0">
                  {customer?.name?.charAt(0) || "•"}
                </div>
              )}
              <span className="font-display font-semibold tracking-tight text-slate-900 dark:text-white truncate min-w-[64px] max-w-[140px] sm:max-w-[240px]">{customer?.name}</span>
              <span className="text-slate-300 dark:text-slate-600 hidden sm:block">×</span>
              <span className="hidden sm:block"><PlenaLogo size="sm" /></span>
            </div>
            {workspace && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="header-project-selector" className="ml-2 h-9 px-3.5 rounded-xl bg-slate-100/80 hover:bg-slate-200/70 dark:bg-white/10 dark:hover:bg-white/15 text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2 transition-colors shrink-0">
                    <FolderKanban size={14} className="text-slate-400" />
                    <span className="max-w-[160px] truncate">{currentProject?.name || "Proje seçin"}</span>
                    <ChevronDown size={13} className="text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="rounded-xl min-w-[220px]">
                  {workspace.projects.map((p) => (
                    <DropdownMenuItem key={p.id} data-testid="project-selector-item" onClick={() => setProjectId(p.id)} className="rounded-lg">
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="text-xs text-slate-400 ml-2">{p.task_count}</span>
                    </DropdownMenuItem>
                  ))}
                  {isAdmin && (
                    <>
                      <DropdownMenuItem data-testid="project-create-menu-item" onClick={() => setNewProjectOpen(true)} className="rounded-lg text-[#0066CC] font-medium">
                        <Plus size={14} className="mr-1" /> Yeni Proje
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="project-import-menu-item" onClick={() => setImportOpen(true)} className="rounded-lg text-[#34C759] font-medium">
                        <FileSpreadsheet size={14} className="mr-1" /> Excel'den Proje Oluştur
                      </DropdownMenuItem>
                      {currentProject && (
                        <DropdownMenuItem data-testid="project-delete-menu-item" onClick={deleteProject} className="rounded-lg text-[#FF2D55] font-medium">
                          <Trash2 size={14} className="mr-1" /> Projeyi Sil
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="bg-slate-200/60 dark:bg-white/10 p-1 rounded-xl flex items-center gap-1 border border-slate-300/40 dark:border-white/10">
              <button data-testid="header-view-switcher-board" onClick={() => setView("board")}
                className={`h-8 px-3.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${view === "board" ? "bg-white shadow-sm text-slate-900 dark:bg-[#3A3A3C] dark:text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}>
                <LayoutGrid size={14} /> <span className="hidden sm:inline">Board</span>
              </button>
              <button data-testid="header-view-switcher-list" onClick={() => setView("list")}
                className={`h-8 px-3.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${view === "list" ? "bg-white shadow-sm text-slate-900 dark:bg-[#3A3A3C] dark:text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}>
                <List size={14} /> <span className="hidden sm:inline">Liste</span>
              </button>
              <button data-testid="header-view-switcher-timeline" onClick={() => setView("timeline")}
                className={`h-8 px-3.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${view === "timeline" ? "bg-white shadow-sm text-slate-900 dark:bg-[#3A3A3C] dark:text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}>
                <GanttChartSquare size={14} /> <span className="hidden sm:inline">Timeline</span>
              </button>
              <button data-testid="header-view-switcher-report" onClick={() => setView("report")}
                className={`h-8 px-3.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${view === "report" ? "bg-white shadow-sm text-slate-900 dark:bg-[#3A3A3C] dark:text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"}`}>
                <BarChart3 size={14} /> <span className="hidden sm:inline">Rapor</span>
              </button>
            </div>
            <button data-testid="theme-toggle-button" onClick={toggle}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors">
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button data-testid="header-logout-button" onClick={logout}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 py-6 overflow-hidden flex flex-col">
        {!workspace ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-[#0066CC] animate-spin" />
          </div>
        ) : !currentProject ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <FolderKanban size={40} className="text-slate-300 mb-4" />
            <p className="text-slate-500 text-sm mb-4">Henüz proje yok.</p>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button data-testid="empty-create-project-button" onClick={() => setNewProjectOpen(true)}
                  className="h-10 px-4 rounded-xl bg-[#0066CC] text-white text-sm font-semibold flex items-center gap-2">
                  <Plus size={15} /> İlk Projeyi Oluştur
                </button>
                <button data-testid="empty-import-project-button" onClick={() => setImportOpen(true)}
                  className="h-10 px-4 rounded-xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 text-sm font-semibold flex items-center gap-2">
                  <FileSpreadsheet size={15} className="text-[#34C759]" /> Excel'den Aktar
                </button>
              </div>
            )}
          </div>
        ) : board ? (
          view === "board" ? (
            <>
              <TaskFilterBar tasks={board.tasks} board={board} filters={filters} onChange={setFilters} visibleCount={visibleIds.size} />
              <KanbanBoard board={board} visibleIds={visibleIds} reload={loadBoard} onTaskClick={openTask} />
            </>
          ) : view === "list" ? (
            <>
              <TaskFilterBar tasks={board.tasks} board={board} filters={filters} onChange={setFilters} visibleCount={visibleIds.size} />
              <ListView board={board} visibleIds={visibleIds} onTaskClick={openTask} reload={loadBoard} />
            </>
          ) : view === "timeline" ? (
            <>
              <TaskFilterBar tasks={board.tasks} board={board} filters={filters} onChange={setFilters} visibleCount={visibleIds.size} />
              <TimelineView board={filteredBoard} onTaskClick={openTask} reload={loadBoard} />
            </>
          ) : (
            <ReportView board={board} onTaskClick={openTask} customer={workspace.customer} />
          )
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-[#0066CC] animate-spin" />
          </div>
        )}
      </main>

      {selectedTask && board && (
        <TaskDetailSheet
          task={board.tasks.find((t) => t.id === selectedTask.id) || selectedTask}
          columns={board.columns}
          board={board}
          onClose={() => setSelectedTask(null)}
          reload={loadBoard}
        />
      )}

      {workspace && (
        <ImportExcelDialog open={importOpen} onClose={() => setImportOpen(false)} customerId={workspace.customer.id}
          onImported={async (p) => { setImportOpen(false); await loadWorkspace(); setProjectId(p.id); }} />
      )}

      <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
        <DialogContent className="rounded-2xl sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Yeni Proje</DialogTitle></DialogHeader>
          <form onSubmit={createProject} className="space-y-4 mt-2">
            <input data-testid="project-name-input" required autoFocus value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)} placeholder="Proje adı"
              className="w-full h-10 px-3.5 rounded-xl bg-slate-100/80 border border-slate-200/60 text-sm focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white transition-all" />
            <button data-testid="project-create-submit-button" type="submit"
              className="w-full h-10 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] text-white text-sm font-semibold transition-all">
              Oluştur
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
